﻿import React, { useState, useEffect } from 'react'
import { FaFolder, FaFolderOpen, FaPlus, FaEdit, FaTrash, FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { useAuth } from '../../contexts/AuthContext'
import { apiCall } from '../../config/api'
import { DocumentTypeCategoryModel } from '../../types/document'

interface SecondaryCategory extends DocumentTypeCategoryModel {
  primary_category: string | null
  primary_category_name: string | null
}

interface PrimaryCategory extends DocumentTypeCategoryModel {
  secondary_categories: SecondaryCategory[]
}

type ModalMode = 'primary' | 'secondary'

const emptyPrimary = (): Partial<PrimaryCategory> => ({ id: undefined, name: '', description: '' })
const emptySecondary = (primaryId?: string): Partial<SecondaryCategory> => ({
  id: undefined, name: '', description: '', primary_category: primaryId ?? null,
})

const DocumentCategories = () => {
  const { token } = useAuth()

  const [primaryCats, setPrimaryCats]         = useState<PrimaryCategory[]>([])
  const [orphanSecondary, setOrphanSecondary] = useState<SecondaryCategory[]>([])
  const [expandedIds, setExpandedIds]         = useState<Set<string>>(new Set())
  const [loading, setLoading]                 = useState(true)

  // modal state
  const [modalMode, setModalMode]       = useState<ModalMode>('primary')
  const [isModalOpen, setIsModalOpen]   = useState(false)
  const [currentItem, setCurrentItem]   = useState<Partial<PrimaryCategory | SecondaryCategory>>(emptyPrimary())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditing, setIsEditing]       = useState(false)

  useEffect(() => { fetchAll() }, [])

  const fetchAll = async () => {
    if (!token) return
    setLoading(true)
    try {
      const [primRes, secRes] = await Promise.all([
        apiCall('/documents/document-type-primary-categories/', { method: 'GET' }, token),
        apiCall('/documents/document-type-secondary-categories/', { method: 'GET' }, token),
      ])
      const primData: PrimaryCategory[] = primRes.ok ? ((await primRes.json()).results ?? await primRes.json()) : []
      const secData: SecondaryCategory[] = secRes.ok ? ((await secRes.json()).results ?? await secRes.json()) : []

      // Merge secondaries into their primaries (API already nests them, but secondary endpoint
      // gives us the freshest data including newly assigned parents)
      const primMap = new Map<string, PrimaryCategory>()
      primData.forEach(p => primMap.set(p.id, { ...p, secondary_categories: [] }))
      const orphans: SecondaryCategory[] = []
      secData.forEach(s => {
        if (s.primary_category && primMap.has(s.primary_category)) {
          primMap.get(s.primary_category)!.secondary_categories.push(s)
        } else {
          orphans.push(s)
        }
      })
      primMap.forEach(p => p.secondary_categories.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')))
      setPrimaryCats([...primMap.values()].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')))
      setOrphanSecondary(orphans.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')))
      // Expand all by default
      setExpandedIds(new Set(primMap.keys()))
    } catch (e) {
      console.error('Failed to load categories', e)
    } finally {
      setLoading(false)
    }
  }

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const openAdd = () => {
    setIsEditing(false)
    setModalMode('primary')
    setCurrentItem(emptyPrimary())
    setIsModalOpen(true)
  }

  // Edit existing — mode is inferred from which type is passed
  const openEditPrimary = (p: PrimaryCategory) => {
    setIsEditing(true)
    setModalMode('primary')
    setCurrentItem({ ...p })
    setIsModalOpen(true)
  }
  const openEditSecondary = (s: SecondaryCategory) => {
    setIsEditing(true)
    setModalMode('secondary')
    setCurrentItem({ ...s })
    setIsModalOpen(true)
  }

  // Quick-add secondary pre-linked to a primary (from the row's + button)
  const openAddSecondaryUnder = (primaryId: string) => {
    setIsEditing(false)
    setModalMode('secondary')
    setCurrentItem(emptySecondary(primaryId))
    setIsModalOpen(true)
  }

  const deletePrimary = async (p: PrimaryCategory) => {
    if (!window.confirm(`Delete primary category "${p.name}"?\n\nIts sub-categories will become uncategorised.`)) return
    const res = await apiCall(`/documents/document-type-primary-categories/${p.id}/`, { method: 'DELETE' }, token!)
    if (res.ok) fetchAll()
    else alert('Failed to delete')
  }
  const deleteSecondary = async (s: SecondaryCategory) => {
    if (!window.confirm(`Delete sub-category "${s.name}"?`)) return
    const res = await apiCall(`/documents/document-type-secondary-categories/${s.id}/`, { method: 'DELETE' }, token!)
    if (res.ok) fetchAll()
    else alert('Failed to delete')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setIsSubmitting(true)
    try {
      const isPrimary = modalMode === 'primary'
      const base = isPrimary ? '/documents/document-type-primary-categories/' : '/documents/document-type-secondary-categories/'
      const url    = (currentItem as any).id ? `${base}${(currentItem as any).id}/` : base
      const method = (currentItem as any).id ? 'PUT' : 'POST'

      const body: Record<string, unknown> = {
        name:        currentItem.name,
        description: currentItem.description || null,
      }
      if (!isPrimary) {
        body.primary_category = (currentItem as SecondaryCategory).primary_category || null
      }

      const res = await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, token)

      if (res.ok) {
        await fetchAll()
        setIsModalOpen(false)
      } else {
        const err = await res.json().catch(() => ({}))
        alert(`Failed to save: ${JSON.stringify(err)}`)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
          <FaFolder className="text-primary" />
          Document Categories
        </h1>
        <button onClick={openAdd} className="btn btn-primary">
          <FaPlus className="w-4 h-4 mr-2" /> Category
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><span className="loading loading-spinner loading-lg" /></div>
      ) : (
        <div className="space-y-3">
          {primaryCats.length === 0 && orphanSecondary.length === 0 && (
            <div className="text-center text-base-content/50 py-16">
              No categories yet. Add a Primary Category to get started.
            </div>
          )}

          {/* Primary categories */}
          {primaryCats.map(primary => {
            const expanded = expandedIds.has(primary.id)
            return (
              <div key={primary.id} className="bg-base-100 rounded-lg shadow border border-base-300">
                {/* Primary row */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    onClick={() => toggleExpand(primary.id)}
                    className="btn btn-ghost btn-xs px-1"
                    title={expanded ? 'Collapse' : 'Expand'}
                  >
                    {expanded ? <FaChevronDown className="w-3 h-3" /> : <FaChevronRight className="w-3 h-3" />}
                  </button>
                  <FaFolderOpen className="text-warning w-5 h-5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-base-content">{primary.name}</span>
                    {primary.description && (
                      <span className="ml-3 text-sm text-base-content/60 truncate">{primary.description}</span>
                    )}
                  </div>
                  <span className="badge badge-ghost badge-sm">
                    {primary.secondary_categories.length} sub-{primary.secondary_categories.length === 1 ? 'category' : 'categories'}
                  </span>
                  <div className="flex gap-1 ml-2">
                    <button
                      onClick={() => openAddSecondaryUnder(primary.id)}
                      className="btn btn-ghost btn-xs text-primary"
                      title="Add sub-category"
                    >
                      <FaPlus className="w-3 h-3" />
                    </button>
                    <button onClick={() => openEditPrimary(primary)} className="btn btn-ghost btn-xs" title="Edit">
                      <FaEdit className="w-3 h-3" />
                    </button>
                    <button onClick={() => deletePrimary(primary)} className="btn btn-ghost btn-xs text-error" title="Delete">
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Secondary rows */}
                {expanded && primary.secondary_categories.length > 0 && (
                  <div className="border-t border-base-300">
                    {primary.secondary_categories.map((sec, idx) => (
                      <div
                        key={sec.id}
                        className={`flex items-center gap-3 px-4 py-2 pl-12 ${idx % 2 === 0 ? 'bg-base-200/40' : ''}`}
                      >
                        <span className="text-base-content/40 text-xs select-none">└</span>
                        <FaFolder className="text-base-content/40 w-4 h-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium">{sec.name}</span>
                          {sec.description && (
                            <span className="ml-3 text-xs text-base-content/50 truncate">{sec.description}</span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => openEditSecondary(sec)} className="btn btn-ghost btn-xs" title="Edit">
                            <FaEdit className="w-3 h-3" />
                          </button>
                          <button onClick={() => deleteSecondary(sec)} className="btn btn-ghost btn-xs text-error" title="Delete">
                            <FaTrash className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {expanded && primary.secondary_categories.length === 0 && (
                  <div className="border-t border-base-300 px-12 py-2 text-sm text-base-content/40 italic">
                    No sub-categories —{' '}
                    <button onClick={() => openAddSecondaryUnder(primary.id)} className="link link-primary">add one</button>
                  </div>
                )}
              </div>
            )
          })}

          {/* Orphan secondary categories (no parent assigned) */}
          {orphanSecondary.length > 0 && (
            <div className="bg-base-100 rounded-lg shadow border border-base-300 border-dashed">
              <div className="px-4 py-3 border-b border-base-300">
                <span className="font-semibold text-base-content/60 text-sm">Uncategorised Sub-Categories</span>
                <span className="ml-2 text-xs text-base-content/40">(not assigned to a primary category)</span>
              </div>
              {orphanSecondary.map((sec, idx) => (
                <div
                  key={sec.id}
                  className={`flex items-center gap-3 px-4 py-2 pl-8 ${idx % 2 === 0 ? 'bg-base-200/40' : ''}`}
                >
                  <FaFolder className="text-base-content/30 w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{sec.name}</span>
                    {sec.description && (
                      <span className="ml-3 text-xs text-base-content/50">{sec.description}</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEditSecondary(sec)} className="btn btn-ghost btn-xs" title="Edit">
                      <FaEdit className="w-3 h-3" />
                    </button>
                    <button onClick={() => deleteSecondary(sec)} className="btn btn-ghost btn-xs text-error" title="Delete">
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-base-100 p-6 rounded-lg shadow-xl w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">
              {isEditing
                ? `Edit ${modalMode === 'primary' ? 'Primary Category' : 'Sub-Category'}`
                : 'Add Category'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">

                {/* Category type selector — only shown when adding, not editing */}
                {!isEditing && (
                  <div>
                    <label className="block mb-1 font-medium">Category Type <span className="text-error">*</span></label>
                    <select
                      className="select select-bordered w-full"
                      value={modalMode}
                      onChange={e => {
                        const next = e.target.value as ModalMode
                        setModalMode(next)
                        setCurrentItem(next === 'primary' ? emptyPrimary() : emptySecondary())
                      }}
                    >
                      <option value="primary">Primary Category</option>
                      <option value="secondary">Sub-Category</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block mb-1 font-medium">
                    Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder={modalMode === 'primary' ? 'e.g. Clinical' : 'e.g. Referral Letters'}
                    value={currentItem.name ?? ''}
                    onChange={e => setCurrentItem({ ...currentItem, name: e.target.value })}
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium">Description</label>
                  <textarea
                    className="textarea textarea-bordered w-full"
                    placeholder="Optional description"
                    value={currentItem.description ?? ''}
                    onChange={e => setCurrentItem({ ...currentItem, description: e.target.value })}
                    rows={2}
                  />
                </div>

                {modalMode === 'secondary' && (
                  <div>
                    <label className="block mb-1 font-medium">Primary Category</label>
                    <select
                      className="select select-bordered w-full"
                      value={(currentItem as SecondaryCategory).primary_category ?? ''}
                      onChange={e => setCurrentItem({
                        ...currentItem,
                        primary_category: e.target.value || null,
                      } as Partial<SecondaryCategory>)}
                    >
                      <option value="">— None (uncategorised) —</option>
                      {primaryCats.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}

              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button type="button" className="btn btn-ghost" onClick={() => setIsModalOpen(false)} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentCategories
